import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Shared load state for the self-fetching business summary widgets.
 *
 * These widgets originally swallowed every fetch error (`.catch(() => {})`) and left `data` null,
 * which renders as a spinner forever. That is reachable on any failure — a 403 from the tenancy
 * guard or from a disabled module, an expired session, a 500 — and module gating only hides the
 * disabled-module case, and only while the gating fetch itself succeeds (it fails open). So the
 * widget has to be able to say it failed.
 *
 * `load` is looked up lazily on each call so a jest.mock of the service object is still honoured.
 */
export function useSummaryFetch<T>(
    load: (institutionId: string) => Promise<T>,
    institutionId: string,
): { data: T | null; failed: boolean } {
    const [data, setData] = useState<T | null>(null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let live = true;
        setData(null);
        setFailed(false);
        load(institutionId)
            .then(x => { if (live) setData(x); })
            .catch(() => { if (live) setFailed(true); });
        return () => { live = false; };
    }, [load, institutionId]);
    return { data, failed };
}

/** Honest terminal state for a summary widget whose fetch failed — never an infinite spinner. */
export const WidgetLoadError: React.FC = () => {
    const { t } = useTranslation('dashboard');
    return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('widgets.loadError')}
        </Typography>
    );
};
